/**
 * Something on the screen that is only there while something is happening.
 *
 * Notices pile up and go away; numbers float up when experience is earned and
 * fade. Both belong somewhere on the screen, both are somebody's preference, and
 * neither can be taken hold of when nothing is happening, which is why holding
 * Alt shows a ghost of each of them where it sits. Drag the ghost and the real
 * thing goes with it, from now on.
 *
 * The position is kept the same way every other window's is, so it is remembered
 * per screen and cannot be dragged off the edge.
 */

import type { ReactNode } from 'react';
import { useDraggableWindow } from './draggable';

export function LoosePanel({
  kind,
  label,
  showing,
  altHeld,
  home,
  children,
}: {
  /** What it is called where it is remembered. */
  kind: string;
  /** What the ghost says, so somebody dragging it knows what they are moving. */
  label: string;
  /** Whether the thing itself has anything to show right now. */
  showing: boolean;
  altHeld: boolean;
  home?: (size: { width: number; height: number }) => { x: number; y: number };
  children: ReactNode;
}): JSX.Element | null {
  const { attach, style, onPointerDown } = useDraggableWindow(kind, { home, grabAnywhere: true });

  // Nothing to show and nobody rearranging: nothing at all, so it costs no
  // pointer and no paint.
  if (!showing && !altHeld) return null;

  return (
    <div
      className={altHeld ? 'loose ui-mode' : 'loose'}
      ref={attach}
      style={style}
      onPointerDown={altHeld ? onPointerDown : undefined}
    >
      {altHeld ? (
        <div className="loose-ghost">
          <span>{label}</span>
          <small>Drag to move</small>
        </div>
      ) : null}

      {showing ? children : null}
    </div>
  );
}
